import { useEffect, useRef, useState, RefObject } from 'react'

interface UseIntersectionObserverOptions extends IntersectionObserverInit {
  triggerOnce?: boolean
  skip?: boolean
}

export function useIntersectionObserver<T extends Element = HTMLDivElement>(
  options: UseIntersectionObserverOptions = {}
): [RefObject<T>, boolean] {
  const elementRef = useRef<T>(null)
  const [isIntersecting, setIsIntersecting] = useState(false)
  const [hasTriggered, setHasTriggered] = useState(false)

  const {
    threshold = 0.1,
    root = null,
    rootMargin = '0px',
    triggerOnce = true,
    skip = false,
  } = options

  useEffect(() => {
    const element = elementRef.current
    if (!element || skip) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isElementIntersecting = entry.isIntersecting
        
        if (isElementIntersecting && !hasTriggered) {
          setIsIntersecting(true)
          if (triggerOnce) {
            setHasTriggered(true)
          }
        } else if (!triggerOnce) {
          setIsIntersecting(isElementIntersecting)
        }
      },
      {
        threshold,
        root,
        rootMargin,
      }
    )

    observer.observe(element)

    return () => {
      observer.unobserve(element)
    }
  }, [threshold, root, rootMargin, triggerOnce, skip, hasTriggered])

  return [elementRef, isIntersecting]
}

// Hook for staggered animations
export function useStaggeredIntersection<T extends Element = HTMLDivElement>(
  delay: number = 100,
  options: UseIntersectionObserverOptions = {}
): [RefObject<T>, boolean, number] {
  const [ref, isIntersecting] = useIntersectionObserver<T>(options)
  const [animationDelay, setAnimationDelay] = useState(0)
  const indexRef = useRef(0)

  useEffect(() => {
    if (isIntersecting) {
      setAnimationDelay(indexRef.current * delay)
      indexRef.current += 1
    }
  }, [isIntersecting, delay])

  return [ref, isIntersecting, animationDelay]
}

// Hook for scroll-triggered animations with progress
export function useScrollProgress<T extends Element = HTMLDivElement>(): [
  RefObject<T>,
  number
] {
  const elementRef = useRef<T>(null)
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    const handleScroll = () => {
      const rect = element.getBoundingClientRect()
      const windowHeight = window.innerHeight
      
      // Calculate progress based on element position
      const elementTop = rect.top
      const elementHeight = rect.height
      
      // Element is fully visible when top is at windowHeight and bottom is at 0
      const startPoint = windowHeight
      const endPoint = -elementHeight
      
      const scrolled = startPoint - elementTop
      const total = startPoint - endPoint
      
      const progressValue = Math.max(0, Math.min(1, scrolled / total))
      setProgress(progressValue)
    }

    handleScroll() // Initial calculation
    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
    }
  }, [])

  return [elementRef, progress]
}